/* eslint-disable react-native/no-inline-styles */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  View,
  Platform,
  AppState,
  NativeEventSubscription,
  ActivityIndicator,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Nav, DocketDetailsProps } from '../../common/types';
import { useSession } from '../../hooks/useSession';
import { useDockets } from '../../hooks/useDockets';
import { useNetworkStatusVar } from '../../common/blocks/NetworkStatus';
import { DocketStatus, PrinterProfile } from '@oolio-group/domain';
import { SegmentControlTabs } from '../../common/types';
import {
  options,
  FEATURES,
  DocketPlacement,
  CourseStatus,
} from '../../common/constants';
import { useColors } from '../../hooks/useColors';
import { useSettings } from '../../hooks/useSettings';
import { useFetchSettings } from '../../hooks/useFetchSettings';
import { useSections } from '../../hooks/organization/useSections';
import { getHeaders, headers } from '../../utils/fetchHeaders';
import {
  openDocketslastUpdatedAt,
  tokenUpdated,
  timeVar,
  statsBarUpdated,
  selectedDocketVar,
  bulkSelectVar,
  autoBumpFeatureVar,
} from '../../state/apolloVars';
import { useReactiveVar } from '@apollo/client';
import DocketsLayout from './Dockets/DocketsLayout';
import MainScreenStyles from './MainScreen.styles';
import ButtonIcon from '../../component/Buttons/ButtonIcon';
import SegmentControl from '../../component/SegmentControl/SegmentControl';
import PrinterProfileModal from './PrinterProfile';
import { useDeviceEvent } from '../../hooks/useDeviceEvent';
import { analyticsService } from '../../analytics/AnalyticsService';
import SortingOptions from '../../component/SortingOptions/SortingOptions';
import ButtonText from '../../component/Buttons/ButtonText';
import { useTranslation } from '@oolio-group/localization';
import AllDayView from '../../component/AllDayView/AllDayView';
import ActionPanel from '../../component/ActionPanel/ActionPanel';
import { Notification, useNotification } from '../../hooks/useNotification';
import { NotificationBanner } from './Notification';
import StatsBar from '../../component/StatsBar/StatsBar';
import DocketDetails from '../../component/DocketDetails/DocketDetails';

const MainScreen: React.FC = () => {
  const appState = useRef(AppState.currentState);

  const { fetchSettings, loading: settingsLoading } = useFetchSettings();
  const {
    sections,
    getSectionsRequest,
    loading: sectionsLoading,
  } = useSections();
  const { session, setSession } = useSession();
  const { translate } = useTranslation();
  const { logout } = useSettings();
  const styles = MainScreenStyles();
  const { colors } = useColors();
  const { activeTheme } = useColors();
  const navigation = useNavigation<Nav>();
  const networkStatus = useNetworkStatusVar();
  const tokenUpdatedVar = useReactiveVar(tokenUpdated);
  const selectedDockets = useReactiveVar(selectedDocketVar);
  const storeId = session?.currentStore?.id || '';
  const orgId = session?.currentOrganization?.id || '';
  const {
    getActiveDockets,
    getCompletedDockets,
    tagDockets,
    untagDockets,
    printKitchenDocket,
    rushDockets,
    unrushDockets,
    moveDocket,
  } = useDockets();
  useDeviceEvent();
  const [headers, setHeaders] = useState<headers>();
  const [currentPage, setCurrentPage] = useState(0);
  const [printerProfileIds, setPrinterProfileIds] = useState<string[]>([]);
  const [selectedTab, setSelectedTab] = useState<SegmentControlTabs>(
    options[0],
  );
  const [selectedCourseStatus, setSelectedCourseStatus] = useState<
    CourseStatus | undefined
  >(undefined);
  const isBulkMode = useReactiveVar(bulkSelectVar);
  const selectedDocketId = Object.keys(selectedDockets)?.[0];

  const [sortViewVisible, setSortViewVisible] = useState<boolean>(false);
  const [allDayViewVisible, setAllDayViewVisible] = useState<boolean>(false);
  const [docketDetailsVisible, setDocketDetailsVisible] =
    useState<boolean>(false);
  const [selectedDocket, setSelectedDocket] = useState<string>();
  const [docketDetails, setDocketDetails] = useState<DocketDetailsProps>();

  const [isDocketPrintEnabled, setIsDocketPrintEnabled] =
    useState<boolean>(false);

  const { currentNotification, closeNotification: closeAppNotification } =
    useNotification();

  const isSidebarVisible = allDayViewVisible || sortViewVisible;
  const loading = sectionsLoading || settingsLoading;

  useEffect(() => {
    if (!session?.sections?.length && sections?.length) {
      setSession({
        sections: sections,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, setSession]);

  useEffect(() => {
    const getDocketPrintFlag = async () => {
      const isPrintEnabled = !!session?.kitchenDisplay?.printer;
      setIsDocketPrintEnabled(isPrintEnabled);
    };
    getDocketPrintFlag();
  }, [session?.kitchenDisplay?.printer]);
  const onLogout = useCallback(() => {
    analyticsService.capture('Logged Out');
    if (session?.kitchenDisplay?.id) logout(session?.kitchenDisplay?.id);
  }, [session, logout]);

  const tabOptions = useMemo(() => {
    const baseOptions: SegmentControlTabs[] = [
      {
        label: 'Open',
        value: DocketStatus.CREATED,
      },
      {
        label: 'Closed',
        value: DocketStatus.COMPLETED,
      },
    ];

    if (session?.kitchenDisplay?.isCourseCallEnabled) {
      baseOptions.push(
        {
          label: 'hold',
          value: CourseStatus.HOLD,
        },
        {
          label: 'away',
          value: CourseStatus.AWAY,
        },
      );
    }

    return baseOptions;
  }, [session?.kitchenDisplay?.isCourseCallEnabled]);

  useEffect(() => {
    if (!session?.kitchenDisplay?.isCourseCallEnabled && selectedCourseStatus) {
      setSelectedCourseStatus(undefined);
      setSelectedTab({ label: 'Open', value: DocketStatus.CREATED });
    }
  }, [session?.kitchenDisplay?.isCourseCallEnabled, selectedCourseStatus]);

  const onMoveDocket = useCallback(
    (placement: DocketPlacement) => {
      moveDocket(selectedDocketId, placement);
    },
    [moveDocket, selectedDocketId],
  );

  const toggleAllDayView = useCallback(() => {
    if (sortViewVisible && !allDayViewVisible) {
      setSortViewVisible(false);
    }
    setAllDayViewVisible(prev => !prev);
  }, [allDayViewVisible, sortViewVisible]);

  const closeAllDayView = useCallback(() => {
    setAllDayViewVisible(false);
  }, []);
  const closeActionPanel = useCallback(() => {
    selectedDocketVar({});
    bulkSelectVar(false);
  }, []);

  const toggleSortView = useCallback(() => {
    if (allDayViewVisible && !sortViewVisible) {
      setAllDayViewVisible(false);
    }
    setSortViewVisible(prev => !prev);
  }, [allDayViewVisible, sortViewVisible]);

  const closeSortView = useCallback(() => {
    setSortViewVisible(false);
  }, []);

  const onPressTab = useCallback(
    (tab: DocketStatus | CourseStatus) => {
      analyticsService.capture(`Viewing ${tab}`);
      if (tab === CourseStatus.HOLD || tab === CourseStatus.AWAY) {
        setSelectedCourseStatus(tab as CourseStatus);
        setSelectedTab({
          label: tab,
          value: DocketStatus.CREATED,
        });
      } else {
        setSelectedCourseStatus(undefined);
        setSelectedTab({
          label: tab,
          value: tab,
        });
      }
      openDocketslastUpdatedAt(performance.now());
      setCurrentPage(0);
      if (tab === DocketStatus.COMPLETED) {
        if (allDayViewVisible) closeAllDayView();
        else if (sortViewVisible) closeSortView();
      }
    },
    [allDayViewVisible, closeAllDayView, closeSortView, sortViewVisible],
  );

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (!session?.sections?.length && session?.currentVenue) {
      getSectionsRequest({ variables: { id: session?.currentVenue.id } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSectionsRequest]);

  useEffect(() => {
    getHeaders().then(setHeaders);
  }, [tokenUpdatedVar.timestamp]);

  const onPressRefresh = useCallback(async () => {
    analyticsService.capture('Refresh Requested');
    if (printerProfileIds?.length) {
      selectedTab.value == DocketStatus.CREATED
        ? await getActiveDockets(printerProfileIds, storeId, orgId)
        : await getCompletedDockets(printerProfileIds, storeId, orgId);
      openDocketslastUpdatedAt(performance.now());
    }
    statsBarUpdated({
      ...statsBarUpdated(),
      lastUpdatedAt: Date.now(),
    });
  }, [
    getActiveDockets,
    getCompletedDockets,
    printerProfileIds,
    selectedTab.value,
    storeId,
    orgId,
  ]);

  const onPressSettings = useCallback(() => {
    analyticsService.capture('Navigated to Settings');
    if (session.authorized) {
      navigation.navigateDeprecated('Settings');
    }
  }, [navigation, session.authorized]);

  const onPressBulkMode = useCallback(() => {
    bulkSelectVar(true);
  }, []);

  useEffect(() => {
    if (printerProfileIds?.length) {
      (async () => {
        try {
          selectedTab.value == DocketStatus.CREATED
            ? await getActiveDockets(printerProfileIds, storeId, orgId)
            : await getCompletedDockets(printerProfileIds, storeId, orgId);
        } catch (e) {
          console.log(e);
        }
      })();
    }
  }, [
    printerProfileIds,
    getActiveDockets,
    getCompletedDockets,
    selectedTab.value,
    storeId,
    orgId,
  ]);

  useEffect(() => {
    const printerProfileIds = session?.kitchenDisplay?.printerProfiles?.map(
      (printerProfile: PrinterProfile) => printerProfile.id,
    );
    if (printerProfileIds?.length) setPrinterProfileIds(printerProfileIds);
  }, [session?.kitchenDisplay?.printerProfiles]);

  useEffect(() => {
    // this block will render when app moves from inactive or background to foreground
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      const subscription: NativeEventSubscription = AppState.addEventListener(
        'change',
        nextAppState => {
          if (
            /inactive|background/.test(appState.current) &&
            nextAppState === 'active'
          ) {
            selectedTab.value == DocketStatus.CREATED
              ? getActiveDockets(printerProfileIds, storeId, orgId)
              : getCompletedDockets(printerProfileIds, storeId, orgId);
          }
          appState.current = nextAppState;
        },
      ) as unknown as NativeEventSubscription;
      return () => {
        if (subscription) {
          subscription.remove();
        }
      };
    }
  }, [
    getActiveDockets,
    getCompletedDockets,
    printerProfileIds,
    selectedTab.value,
    storeId,
    orgId,
  ]);

  useEffect(() => {
    const interval = setInterval(() => timeVar(Date.now()), 5000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    analyticsService.identify(session.currentOrganization?.id as string, {
      id: session?.currentOrganization?.id,
      name: session?.currentOrganization?.name,
      venue: session?.currentVenue?.name,
      store: session?.currentStore?.name,
      theme: activeTheme,
    });

    // Wait for PostHog to fetch user-specific feature flags after identify
    const timeoutId = setTimeout(async () => {
      const isEnabled = await analyticsService.isFeatureEnabled(
        FEATURES.AUTO_BUMP_FEATURE,
      );
      autoBumpFeatureVar(isEnabled);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    activeTheme,
    session.currentOrganization?.id,
    session.currentOrganization?.name,
    session.currentVenue?.name,
    session.currentStore?.name,
  ]);

  const closeNotification = useCallback(
    (notification: Notification) => {
      closeAppNotification(notification.id);
    },
    [closeAppNotification],
  );

  const onToggleDocketDetails = useCallback(
    ({
      docketId,
      orderNumber,
      items,
      notifierItems,
      status,
      orderTypeId,
      displayStyle,
      allergens,
    }: DocketDetailsProps) => {
      setSelectedDocket(docketId);
      setDocketDetails({
        docketId,
        orderNumber,
        items,
        notifierItems,
        status,
        orderTypeId,
        displayStyle,
        allergens,
      });
      setDocketDetailsVisible(!docketDetailsVisible);
    },
    [
      docketDetailsVisible,
      setSelectedDocket,
      setDocketDetails,
      setDocketDetailsVisible,
    ],
  );

  const closeDocketDetails = useCallback(() => {
    setDocketDetailsVisible(false);
  }, []);

  if (loading)
    return (
      <View style={styles.loader}>
        <ActivityIndicator size={'large'} color={colors.text} />
      </View>
    );

  return (
    <>
      <View style={styles.screen} ph-label="main-screen">
        {currentNotification && (
          <NotificationBanner
            notification={currentNotification}
            closeNotification={closeNotification}
          />
        )}
        <View style={styles.header}>
          <View style={styles.buttons}>
            <ButtonIcon
              ignoreTheme
              testID="btn-networkStatus"
              onPress={onPressRefresh}
              icon={!networkStatus ? 'wifi-slash' : 'wifi'}
              type={!networkStatus ? 'negative' : 'positive'}
            />
            <StatsBar
              headers={headers}
              printerProfileIds={printerProfileIds}
              storeId={storeId}
            />

            <ButtonIcon
              ignoreTheme
              icon="sync"
              testID="btn-refresh"
              type="interface"
              onPress={onPressRefresh}
            />
          </View>
          <View style={[styles.buttons, { justifyContent: 'flex-end' }]}>
            <SegmentControl
              testID="segments-docketType"
              tabs={tabOptions}
              onChange={onPressTab}
              selectedTab={selectedCourseStatus || selectedTab.value}
              disabled={isBulkMode || !!selectedDocketId}
            />
            {selectedTab.value === DocketStatus.CREATED ? (
              <>
                <ButtonText
                  ignoreTheme
                  testID="btn-sort"
                  icon="sort-amount-down"
                  type={sortViewVisible ? 'active' : 'interface'}
                  onPress={toggleSortView}
                  disabled={isBulkMode || !!selectedDocketId}
                  label={
                    session.sortingPref?.label ||
                    translate('kitchenDisplay.sortOldest')
                  }
                  containerStyle={styles.btnSort}
                />
                {
                  <ButtonIcon
                    ignoreTheme
                    disabled={isBulkMode || !!selectedDocketId}
                    icon="eye"
                    testID="btn-all-day-view"
                    onPress={toggleAllDayView}
                    type={allDayViewVisible ? 'active' : 'interface'}
                  />
                }
              </>
            ) : null}

            {selectedTab.value === DocketStatus.CREATED ? (
              <ButtonIcon
                ignoreTheme
                icon="check-circle"
                testID="btn-bulk-select"
                type={isBulkMode ? 'active' : 'interface'}
                onPress={onPressBulkMode}
              />
            ) : null}

            <ButtonIcon
              ignoreTheme
              icon="cog"
              testID="btn-settings"
              type="interface"
              onPress={onPressSettings}
            />
            <ButtonIcon
              ignoreTheme
              testID="btn-logout"
              icon="lock-alt"
              type="negativeLight"
              onPress={onLogout}
            />
          </View>
        </View>
        <View
          style={[styles.content, isSidebarVisible && styles.sidebarMargin]}
        >
          {headers && printerProfileIds?.length ? (
            <DocketsLayout
              headers={headers}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              docketStatus={selectedTab.value}
              courseStatus={selectedCourseStatus}
              enablePrint={isDocketPrintEnabled}
              onToggleDocketDetails={onToggleDocketDetails}
              setDocketDetails={setDocketDetails}
              selectedDocket={selectedDocket}
              closeDocketDetails={closeDocketDetails}
            />
          ) : null}
        </View>
        <PrinterProfileModal />
      </View>
      <AllDayView visible={allDayViewVisible} onClose={closeAllDayView} />
      <ActionPanel
        visible={isBulkMode || !!selectedDocketId}
        onClose={closeActionPanel}
        selectedTab={selectedTab.value as DocketStatus}
        tagDockets={tagDockets}
        untagDockets={untagDockets}
        rushDockets={rushDockets}
        unrushDockets={unrushDockets}
        printKitchenDocket={printKitchenDocket}
        onMoveDocket={onMoveDocket}
        enablePrint={isDocketPrintEnabled}
      />
      {sortViewVisible && selectedTab.value === DocketStatus.CREATED ? (
        <SortingOptions onClose={closeSortView} />
      ) : null}
      {docketDetailsVisible && docketDetails && (
        <DocketDetails
          onClose={closeDocketDetails}
          docketDetails={docketDetails}
        />
      )}
    </>
  );
};

export default MainScreen;
